import { isObject, isString } from "@common/helpers/typeGuards";
import { Counterparty } from "@common/types/counterparty";
import {
    RuleActionType,
    RuleConditionDate,
    RuleConditionType,
    TransactionRule,
    TransactionRuleCondition,
} from "@common/types/transactionRule";
import { FinancialAccount } from "@common/types/financialAccount";
import { Subaccount } from "@common/types/subaccount";
import { Entity } from "@common/types/entity";
import { getAccountNameWithLast4 } from "@common/helpers/financialAccount";
import { ClassWithChildren } from "@helpers/class";
import { getSubaccountDisplayName } from "@design-system/Subaccount/utils";
import { CategoryLabelGetter } from "@hooks/useCategoryLabelGetter";

function toIdMap<T extends { id: string | number }>(
    items: T[],
): Map<string, T> {
    return new Map(items.map((item) => [String(item.id), item]));
}

interface LookupMaps {
    accountById: Map<string, FinancialAccount>;
    subaccountById: Map<string, Subaccount>;
    entityById: Map<string, Entity>;
}

interface GetSearchableLabelOptions {
    labelGetter: CategoryLabelGetter;
    counterparties: Counterparty[];
    financialAccounts: FinancialAccount[];
    entities: Entity[];
    classesMap: Map<string, ClassWithChildren>;
}

interface ConditionLabelOptions {
    counterparties: Counterparty[];
    labelGetter: CategoryLabelGetter;
    classesMap: Map<string, ClassWithChildren>;
    lookupMaps: LookupMaps;
}

export function getSearchableLabel(
    rule: Pick<TransactionRule, "actions" | "conditions">,
    {
        labelGetter,
        financialAccounts,
        entities,
        ...rest
    }: GetSearchableLabelOptions,
): string {
    const categoryAction = rule.actions.find(
        (action) => action.actionType === RuleActionType.CATEGORY,
    );
    const categoryLabel = categoryAction?.value
        ? labelGetter(categoryAction.value as string)
        : "";

    const lookupMaps: LookupMaps = {
        accountById: toIdMap(financialAccounts),
        subaccountById: toIdMap(
            financialAccounts
                .flatMap((account) => account.subaccounts)
                .filter((s): s is NonNullable<typeof s> => s != null),
        ),
        entityById: toIdMap(entities),
    };

    return rule.conditions
        .map((condition) =>
            getConditionSearchableLabel(condition, {
                labelGetter,
                lookupMaps,
                ...rest,
            }),
        )
        .concat(categoryLabel)
        .join(" ");
}

function getConditionSearchableLabel(
    condition: TransactionRuleCondition,
    {
        counterparties,
        labelGetter,
        classesMap,
        lookupMaps,
    }: ConditionLabelOptions,
): string {
    switch (condition.conditionType) {
        case RuleConditionType.COUNTERPARTY:
            return getCounterpartyLabel(condition, counterparties);
        case RuleConditionType.FINANCIAL_ACCOUNTS:
            return getFinancialAccountsLabel(condition, lookupMaps.accountById);
        case RuleConditionType.SUBACCOUNTS:
            return getSubaccountsLabel(condition, lookupMaps.subaccountById);
        case RuleConditionType.ENTITY:
            return getEntitiesLabel(condition, lookupMaps.entityById);
        case RuleConditionType.DESCRIPTION_INCLUDES:
        case RuleConditionType.DESCRIPTION_DOES_NOT_INCLUDE:
        case RuleConditionType.DESCRIPTION_IS:
            return getDescriptionLabel(condition);
        case RuleConditionType.AMOUNT:
            return getAmountLabel(condition);
        case RuleConditionType.DATE:
            return getDateLabel(condition);
        case RuleConditionType.CATEGORY:
        case RuleConditionType.SUGGESTED_CATEGORY:
            return getCategoryLabel(condition, labelGetter);
        case RuleConditionType.CLASS:
            return getClassLabel(condition, classesMap);
        case RuleConditionType.AMOUNT_IS_ROUND:
            return getAmountRoundingLabel(condition);
        case RuleConditionType.AMOUNT_IS_NOT_ROUND:
            return getAmountRoundingLabel(condition);
        case RuleConditionType.DATE_PROXIMITY:
            return "";
        default:
            return "";
    }
}

function getClassLabel(
    condition: TransactionRuleCondition,
    classesMap: Map<string, ClassWithChildren>,
): string {
    if (!Array.isArray(condition.value)) {
        return "";
    }

    const selectedClasses = condition.value as string[];

    return selectedClasses.map((id) => classesMap.get(id)?.label).join(" ");
}

function getCounterpartyLabel(
    condition: TransactionRuleCondition,
    counterparties: Counterparty[],
): string {
    return (
        counterparties.find(
            (counterparty) => counterparty.id === condition.value,
        )?.name ?? ""
    );
}

function getFinancialAccountsLabel(
    condition: TransactionRuleCondition,
    accountById: Map<string, FinancialAccount>,
): string {
    if (!Array.isArray(condition.value)) {
        return "";
    }
    return condition.value
        .map((accountId) => accountById.get(String(accountId)))
        .filter((a): a is FinancialAccount => a !== undefined)
        .map((account) => getAccountNameWithLast4(account))
        .join(" ");
}

function getSubaccountsLabel(
    condition: TransactionRuleCondition,
    subaccountById: Map<string, Subaccount>,
): string {
    if (!Array.isArray(condition.value)) {
        return "";
    }

    return condition.value
        .map((subaccountId) => subaccountById.get(String(subaccountId)))
        .filter((s): s is Subaccount => s !== undefined)
        .map((subaccount) => getSubaccountDisplayName(subaccount))
        .join(" ");
}

function getEntitiesLabel(
    condition: TransactionRuleCondition,
    entityById: Map<string, Entity>,
): string {
    if (!Array.isArray(condition.value)) {
        return "";
    }
    return condition.value
        .map((entityId) => entityById.get(String(entityId)))
        .filter((e): e is Entity => e !== undefined)
        .map((entity) => entity.name)
        .join(" ");
}

function getDescriptionLabel(condition: TransactionRuleCondition): string {
    if (isString(condition.value)) {
        return condition.value;
    }
    if (Array.isArray(condition.value)) {
        return condition.value.join(" ");
    }

    return "";
}

function getDateLabel(condition: TransactionRuleCondition): string {
    if (!isObject(condition.value) || Array.isArray(condition.value)) {
        return "";
    }

    const { after, before } = condition.value as RuleConditionDate;

    if (after && before) {
        return `${after} ${before}`;
    }
    if (after) {
        return after;
    }
    if (before) {
        return before;
    }
    return "";
}

function getAmountLabel(condition: TransactionRuleCondition): string {
    if (!isObject(condition.value) || Array.isArray(condition.value)) {
        return "";
    }

    const amounts: number[] = [];
    if ("min" in condition.value && condition.value.min) {
        amounts.push(condition.value.min);
    }
    if ("max" in condition.value && condition.value.max) {
        amounts.push(condition.value.max);
    }

    return amounts.join(" ");
}

function getAmountRoundingLabel(condition: TransactionRuleCondition): string {
    if (isObject(condition.value) && "level" in condition.value) {
        const { level, isCredit } = condition.value as {
            level: string;
            isCredit: boolean;
        };
        const direction = isCredit ? "cash in" : "cash out";
        if (condition.conditionType === RuleConditionType.AMOUNT_IS_NOT_ROUND) {
            return `amount ${direction} not round`;
        }
        return `amount ${direction} rounding ${level}`;
    }
    return "";
}

function getCategoryLabel(
    condition: TransactionRuleCondition,
    labelGetter: CategoryLabelGetter,
): string {
    if (isString(condition.value)) {
        return labelGetter(condition.value);
    }

    if (Array.isArray(condition.value)) {
        return condition.value.map((id) => labelGetter(id as string)).join(" ");
    }

    return "";
}
